import type { CoachingSession } from '@efdigital/ll-addon-data';
import type AbilityService from '@efdigital/ll-admin-v2/services/ability';
import { getSessionAdminCapabilities } from '@efdigital/ll-admin-v2/utils/coaching/coaching-sessions/admin-capabilities';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import { SessionAbandonModal } from './session-abandon-modal.gts';
import { SessionCancelModal } from './session-cancel-modal.gts';
import { SessionClearExternalReviewModal } from './session-clear-external-review-modal.gts';
import { SessionRescheduleModal } from './session-reschedule-modal.gts';

interface SessionPrimaryActionSignature {
  Args: {
    coachingSession: CoachingSession;
    /** Refresh in place — the session record still exists. */
    onSuccess: () => Promise<void>;
    /** The session record no longer exists — navigate away. */
    onAbandoned: () => Promise<void>;
  };
  Element: HTMLDivElement;
}

/**
 * Admin actions that apply to a session's current status, rendered in a
 * single consistent spot (the Overview card). `booking_pending` only ever
 * offers abandon; a `scheduled` session offers both cancel and reschedule
 * side by side, since either may apply at once; `external_change_flagged`
 * offers dismissing the flag back to `scheduled` — add further branches here
 * as more admin actions are built, rather than scattering status-gated
 * buttons around the page.
 */
export class SessionPrimaryAction extends Component<SessionPrimaryActionSignature> {
  @service declare ability: AbilityService;

  get capabilities() {
    return getSessionAdminCapabilities(
      this.args.coachingSession,
      this.ability.canEdit('coaching-sessions')
    );
  }

  get hasActions(): boolean {
    const {
      canForceAbandon,
      canCancel,
      canReschedule,
      canClearExternalReview,
    } = this.capabilities;
    return (
      canForceAbandon || canCancel || canReschedule || canClearExternalReview
    );
  }

  <template>
    {{#if this.hasActions}}
      <div class="card-footer bg-transparent" ...attributes>
        <div class="d-flex flex-wrap gap-2">
          {{#if this.capabilities.canForceAbandon}}
            <SessionAbandonModal
              @coachingSession={{@coachingSession}}
              @onSuccess={{@onSuccess}}
              @onAbandoned={{@onAbandoned}}
            >
              <:abandon as |showModal|>
                <button
                  type="button"
                  class="btn btn-outline-danger btn-sm"
                  data-test-session-action="abandon"
                  {{on "click" showModal}}
                >Abandon booking…</button>
              </:abandon>
            </SessionAbandonModal>
          {{/if}}
          {{#if this.capabilities.canReschedule}}
            <SessionRescheduleModal
              @coachingSession={{@coachingSession}}
              @onSuccess={{@onSuccess}}
            >
              <:reschedule as |showModal|>
                <button
                  type="button"
                  class="btn btn-outline-primary btn-sm"
                  data-test-session-action="reschedule"
                  {{on "click" showModal}}
                >Reschedule…</button>
              </:reschedule>
            </SessionRescheduleModal>
          {{/if}}
          {{#if this.capabilities.canClearExternalReview}}
            <SessionClearExternalReviewModal
              @coachingSession={{@coachingSession}}
              @onSuccess={{@onSuccess}}
            >
              <:clearExternalReview as |showModal|>
                <button
                  type="button"
                  class="btn btn-outline-primary btn-sm"
                  data-test-session-action="clear-external-review"
                  {{on "click" showModal}}
                >Mark as still scheduled…</button>
              </:clearExternalReview>
            </SessionClearExternalReviewModal>
          {{/if}}
          {{#if this.capabilities.canCancel}}
            <SessionCancelModal
              @coachingSession={{@coachingSession}}
              @onSuccess={{@onSuccess}}
            >
              <:cancel as |showModal|>
                <button
                  type="button"
                  class="btn btn-outline-danger btn-sm"
                  data-test-session-action="cancel"
                  {{on "click" showModal}}
                >Cancel session…</button>
              </:cancel>
            </SessionCancelModal>
          {{/if}}
        </div>
      </div>
    {{/if}}
  </template>
}
